import OrganizationService from "@/services/api/organization";
import type { OrganizationSettings } from "@/services/api/organization";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface OrganizationState {
  settings: OrganizationSettings | null;
  loading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  settings: null,
  loading: false,
  error: null,
};

export const fetchOrgSettings = createAsyncThunk<
  OrganizationSettings,
  void,
  { rejectValue: string }
>("organization/fetchSettings", async (_, { rejectWithValue }) => {
  try {
    return await OrganizationService.getOrganizationSettings();
  } catch (err: any) {
    const message = err.response?.data?.message || err.message || "Failed to fetch organization settings";
    return rejectWithValue(message);
  }
});

export const updateOrgSettings = createAsyncThunk<
  OrganizationSettings,
  FormData,
  { rejectValue: string }
>("organization/updateSettings", async (formData, { rejectWithValue }) => {
  try {
    return await OrganizationService.updateOrganizationSettings(formData);
  } catch (err: any) {
    const message = err.response?.data?.message || err.message || "Failed to update organization settings";
    return rejectWithValue(message);
  }
});

const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    clearSettings: (state) => {
      state.settings = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch settings
      .addCase(fetchOrgSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrgSettings.fulfilled, (state, action: PayloadAction<OrganizationSettings>) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchOrgSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load settings";
      })
      // update settings
      .addCase(updateOrgSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrgSettings.fulfilled, (state, action: PayloadAction<OrganizationSettings>) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(updateOrgSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update settings";
      });
  },
});

export const { clearSettings } = organizationSlice.actions;
export default organizationSlice.reducer;
export type { OrganizationState };
